import React, { useState, useRef, useEffect, JSX } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTheme } from "../context/ThemeContext";

type DropdownOption = {
  label: string;
  checked?: boolean;
  render?: JSX.Element;
};

type ActionButtonProps = {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary";
  textColor?: string;
  dropdownOptions?: DropdownOption[];
  onOptionChange?: (index: number, checked: boolean) => void;
};

type TableActionsProps = {
  buttons?: ActionButtonProps[];
  buttonMarginRight?: string | number;
  buttonGap?: number | string;
  outerMarginLeft?: string | number;
  outerMarginRight?: string | number;
  fullBleedLine?: boolean;
  marginTop?: number | string;
};

const LAYOUT_SIDE_PADDING = 20;

const clamp = (value: number, min = 0, max = 255) =>
  Math.max(min, Math.min(max, value));

const adjustHex = (hex: string, amount: number) => {
  const normalized = hex.replace("#", "").trim();

  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized;

  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);

  const nr = clamp(r + amount);
  const ng = clamp(g + amount);
  const nb = clamp(b + amount);

  return `#${nr.toString(16).padStart(2, "0")}${ng
    .toString(16)
    .padStart(2, "0")}${nb.toString(16).padStart(2, "0")}`;
};

const TableActions: React.FC<TableActionsProps> = ({
  buttons = [],
  buttonMarginRight,
  buttonGap = 8,
  outerMarginLeft,
  outerMarginRight,
  fullBleedLine = false,
  marginTop = 0,
}) => {
  const { departmentColor } = useTheme();

  const baseColor = departmentColor || "#0c353e";

  const FIXED_COLORS = {
    primary: baseColor,
    primaryHover: adjustHex(baseColor, 12),
    primaryActive: adjustHex(baseColor, -14),

    secondary: adjustHex(baseColor, 18),
    secondaryHover: adjustHex(baseColor, 28),
    secondaryActive: adjustHex(baseColor, -8),

    confirm: adjustHex(baseColor, 6),
    confirmHover: adjustHex(baseColor, 16),
    confirmActive: adjustHex(baseColor, -18),

    border: adjustHex(baseColor, 22),
    dropdownBorder: adjustHex(baseColor, 14),
    dropdownText: adjustHex(baseColor, -26),
    dropdownBg: "#ffffff",
  };

  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [fontSize, setFontSize] = useState("13px");
  const [buttonHeight, setButtonHeight] = useState(34);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setFontSize(`${Math.max(12, Math.min(14, w / 135))}px`);
      setButtonHeight(w < 1000 ? 32 : w < 1400 ? 34 : 36);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const parseMargin = (val?: string | number): string =>
    val !== undefined ? (typeof val === "number" ? `${val}px` : val) : "0px";

  const outerRight = parseMargin(outerMarginRight);
  const outerLeft = parseMargin(outerMarginLeft);
  const mt = typeof marginTop === "number" ? `${marginTop}px` : marginTop;

  const wrapperWidth = fullBleedLine
    ? `calc(100% + ${LAYOUT_SIDE_PADDING * 2}px)`
    : "100%";

  const wrapperLeft = fullBleedLine ? `-${LAYOUT_SIDE_PADDING}px` : "0px";

  const applyHoverStyle = (
    el: HTMLButtonElement,
    bg: string,
    shadow: string
  ) => {
    el.style.transform = "translateY(-1px)";
    el.style.filter = "brightness(1.03)";
    el.style.backgroundColor = bg;
    el.style.boxShadow = shadow;
  };

  const applyLeaveStyle = (
    el: HTMLButtonElement,
    bg: string,
    shadow: string
  ) => {
    el.style.transform = "translateY(0)";
    el.style.filter = "none";
    el.style.backgroundColor = bg;
    el.style.boxShadow = shadow;
  };

  const applyDownStyle = (el: HTMLButtonElement, bg: string) => {
    el.style.transform = "translateY(1px)";
    el.style.filter = "brightness(0.97)";
    el.style.backgroundColor = bg;
  };

  const applyUpStyle = (el: HTMLButtonElement, bg: string, shadow: string) => {
    el.style.transform = "translateY(-1px)";
    el.style.filter = "brightness(1.03)";
    el.style.backgroundColor = bg;
    el.style.boxShadow = shadow;
  };

  return (
    <div
      style={{
        ...styles.wrapper,
        marginTop: mt,
        marginLeft: outerLeft,
        marginRight: outerRight,
        position: "relative",
        width: wrapperWidth,
        left: wrapperLeft,
      }}
    >
      <div style={styles.actionsRow}>
        <div style={{ ...styles.buttons, gap: buttonGap }}>
          {buttons.map((btn, idx) => {
            const isSecondary = btn.variant === "secondary";

            const baseBg = isSecondary
              ? FIXED_COLORS.secondary
              : FIXED_COLORS.primary;
            const hoverBg = isSecondary
              ? FIXED_COLORS.secondaryHover
              : FIXED_COLORS.primaryHover;
            const activeBg = isSecondary
              ? FIXED_COLORS.secondaryActive
              : FIXED_COLORS.primaryActive;
            const baseShadow = isSecondary
              ? "0 4px 12px rgba(12, 53, 62, 0.16)"
              : "0 5px 14px rgba(12, 53, 62, 0.18)";
            const hoverShadow = isSecondary
              ? "0 8px 18px rgba(12, 53, 62, 0.22)"
              : "0 9px 20px rgba(12, 53, 62, 0.24)";

            return (
              <div
                key={idx}
                style={{
                  position: "relative",
                  marginRight: buttonMarginRight ?? 0,
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    btn.dropdownOptions
                      ? setOpenDropdown(openDropdown === idx ? null : idx)
                      : btn.onClick()
                  }
                  style={{
                    ...styles.button,
                    height: buttonHeight,
                    fontSize,
                    backgroundColor: baseBg,
                    border: `1px solid ${FIXED_COLORS.border}`,
                    boxShadow: baseShadow,
                    color: btn.textColor || "#FFFFFF",
                  }}
                  onMouseEnter={(e) =>
                    applyHoverStyle(e.currentTarget, hoverBg, hoverShadow)
                  }
                  onMouseLeave={(e) =>
                    applyLeaveStyle(e.currentTarget, baseBg, baseShadow)
                  }
                  onMouseDown={(e) => applyDownStyle(e.currentTarget, activeBg)}
                  onMouseUp={(e) =>
                    applyUpStyle(e.currentTarget, hoverBg, hoverShadow)
                  }
                >
                  {btn.label}
                </button>

                {btn.dropdownOptions && openDropdown === idx && (
                  <div
                    ref={dropdownRef}
                    style={{
                      ...styles.dropdown,
                      top: buttonHeight + 10,
                      border: `1px solid ${FIXED_COLORS.dropdownBorder}`,
                      background: FIXED_COLORS.dropdownBg,
                    }}
                  >
                    <div
                      style={{
                        maxHeight: 300,
                        overflowY: "auto",
                      }}
                    >
                      {btn.dropdownOptions.map((opt, optIdx) => (
                        <label
                          key={optIdx}
                          style={{
                            ...styles.option,
                            color: FIXED_COLORS.dropdownText,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!opt.checked}
                            onChange={(e) =>
                              btn.onOptionChange?.(optIdx, e.target.checked)
                            }
                          />
                          <span>{opt.label}</span>
                          {opt.render && (
                            <span style={{ marginLeft: "auto" }}>
                              {opt.render}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setOpenDropdown(null);
                        btn.onClick();
                      }}
                      style={{
                        ...styles.button,
                        ...styles.confirmButton,
                        height: buttonHeight,
                        fontSize,
                        backgroundColor: FIXED_COLORS.confirm,
                        border: `1px solid ${FIXED_COLORS.border}`,
                        boxShadow: "0 5px 14px rgba(12, 53, 62, 0.18)",
                        color: btn.textColor || "#FFFFFF",
                      }}
                      onMouseEnter={(e) =>
                        applyHoverStyle(
                          e.currentTarget,
                          FIXED_COLORS.confirmHover,
                          "0 9px 20px rgba(12, 53, 62, 0.24)"
                        )
                      }
                      onMouseLeave={(e) =>
                        applyLeaveStyle(
                          e.currentTarget,
                          FIXED_COLORS.confirm,
                          "0 5px 14px rgba(12, 53, 62, 0.18)"
                        )
                      }
                      onMouseDown={(e) =>
                        applyDownStyle(
                          e.currentTarget,
                          FIXED_COLORS.confirmActive
                        )
                      }
                      onMouseUp={(e) =>
                        applyUpStyle(
                          e.currentTarget,
                          FIXED_COLORS.confirmHover,
                          "0 9px 20px rgba(12, 53, 62, 0.24)"
                        )
                      }
                    >
                      Gerar PDF
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  wrapper: {
    width: "100%",
  },

  actionsRow: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },

  buttons: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    paddingBottom: 5,
    paddingRight: 0,
    marginLeft: 24,
  },

  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    fontFamily: "'Open Sans', Inter, sans-serif",
    fontWeight: 400,
    cursor: "pointer",
    userSelect: "none",
    padding: "0 16px",
    borderRadius: 0,
    marginBottom: 10,
    outline: "none",
    letterSpacing: "0.005em",
    whiteSpace: "nowrap",
    transition:
      "transform 160ms ease, box-shadow 200ms ease, filter 200ms ease, background-color 200ms ease, border-color 200ms ease",
    WebkitTapHighlightColor: "transparent",
  },

  dropdown: {
    position: "absolute",
    right: 0,
    borderRadius: 12,
    padding: 10,
    boxShadow: "0 12px 28px rgba(0,0,0,0.14)",
    zIndex: 20,
    width: 260,
  },

  option: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
    cursor: "pointer",
    padding: "6px 4px",
  },

  confirmButton: {
    marginTop: 10,
    width: "100%",
  },
};

export default TableActions;
